import {
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
} from 'ag-grid-community';
import { INVESTMENT_ADVISOR_MEMBER } from 'constants/memberTypes';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { useCallback, useMemo } from 'react';
import { fetchAccountsV2Promise } from 'reducers/accountsReducer';
import { getSelectedMember } from 'reducers/membersReducer';
import {
  fetchUsersPromiseCreator,
  getMemberUserTypes,
} from 'reducers/usersReducer';
import { GetAccountsV2Response } from 'ts/api/accounts_v2';
import Member from 'ts/models/Member.model';
import parseAGRequest from 'utils/agGrid/ssrm/parseAGRequest';
import get from 'lodash/get';
import { ServerSideFilter } from 'utils/agGrid/types';
import UsersResponse from 'ts/api/users';
import MemberUserTypesResponse from 'ts/api/member_user_types';
import { CLEARING } from 'constants/accountTypes';
import BalanceGridRow from '../types';

const useDatasource = () => {
  const dispatch = useAppDispatch();

  const selectedMember: Member = useAppSelector(getSelectedMember);

  const memberUserTypes: MemberUserTypesResponse =
    useAppSelector(getMemberUserTypes);

  const isIRA = useMemo(() => {
    return selectedMember?.type === INVESTMENT_ADVISOR_MEMBER.type;
  }, [selectedMember.type]);

  const getAccounts = useCallback(
    async (req: IServerSideGetRowsRequest) => {
      const body = parseAGRequest(req, { transformAttrs: true });
      let filter: ServerSideFilter[] = [];
      const userAccounts = selectedMember?.ledgerAccounts;

      if (!userAccounts) {
        filter = filter.concat([
          { attr: 'account_type', op: 'eq', value: CLEARING },
        ]);
      }
      if (isIRA) {
        // Since we can't get accounts by memberUser, we fetch the member users
        // and map the account Ids linked to them
        const userFilters = [
          {
            attr: 'type',
            op: 'eq',
            value: memberUserTypes.map((el) => el['value']),
          },
        ];

        userFilters.push({
          attr: 'user_id',
          op: 'eq',
          value: selectedMember?.memberUsers || [],
        });

        let authorizedUsersAccountList: string[] = [];
        let completeUserAccountList = userAccounts ? [...userAccounts] : [];

        const userResult: UsersResponse = await fetchUsersPromiseCreator(
          {
            limit: body?.limit,
            offset: body?.offset,
            page: body?.page,
            sort: body?.sort,
            filter: userFilters,
          },
          dispatch,
        );

        const users = userResult?.users || [];
        // Get list of account IDs owned by the member users
        authorizedUsersAccountList = users.reduce((ids, acc) => {
          const memberUserAccounts = get(acc, 'accountLinks', []);
          return [
            ...ids,
            ...memberUserAccounts.map((account) => account.accountId),
          ];
        }, [] as string[]);
        // The complete account list has the accounts of the
        // and the accounts of the member users assigned to it
        completeUserAccountList = [
          ...completeUserAccountList,
          ...authorizedUsersAccountList,
        ];

        if (userAccounts || completeUserAccountList.length > 0) {
          filter = filter.concat([
            {
              attr: 'account_id',
              value: completeUserAccountList,
              op: 'eq',
            },
          ]);
        }

        const result: GetAccountsV2Response = await fetchAccountsV2Promise(
          {
            ...body,
            filter: [...filter, ...(body?.filter ? body.filter : [])],
            showUsers: true,
          },
          dispatch,
        );
        return result;
      } else {
        if (userAccounts) {
          filter = filter.concat([
            { attr: 'account_id', value: userAccounts, op: 'eq' },
          ]);
        }

        const result: GetAccountsV2Response = await fetchAccountsV2Promise(
          {
            ...body,
            filter: [...filter, ...(body?.filter ? body.filter : [])],
            showUsers: true,
          },
          dispatch,
        );
        return result;
      }
    },
    [
      dispatch,
      isIRA,
      memberUserTypes,
      selectedMember?.ledgerAccounts,
      selectedMember?.memberUsers,
    ],
  );

  const datasource: IServerSideDatasource = useMemo(
    () => ({
      getRows: async ({
        request,
        success,
      }: IServerSideGetRowsParams<BalanceGridRow>) => {
        const accounts = await getAccounts(request);
        success({
          rowData: accounts.accounts ?? [],
          rowCount: accounts.count ?? undefined,
        });
      },
    }),
    [getAccounts],
  );
  return datasource;
};

export default useDatasource;
